import { useState } from 'react';
import GpanelLogo from '../assets/gpanel-logo-dark.png';
import GpanelLogoWhite from '../assets/gpanel-logo.png';
import {
  getUserPortalSettingsForUserQuery,
  saveViewType,
  UserKeys,
} from '../api/users-api';
import { UserSettings } from '../../types';
import { useStore } from '../components/state/zustand-store';
import NotificationPopout from '../components/navbar/notification-popout';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import UserPopout from '../components/navbar/user-popout';
import { useUserSettings } from '../utils/use-user-settings';
import TrialButton from '../components/trial/trial-button';
import SearchDialog from '../components/navbar/search-dialog';
import React from 'react';
import UserAvatar from './user-avatar';
import SupportAgentNavToggle from '../components/support-agent/support-agent-nav-toggle';

import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';

interface TopNavProps {
  isUserPortal: boolean;
}

export default function TopNav({ isUserPortal }: TopNavProps) {
  const queryClient = useQueryClient();
  const { currentTier, loggedInUser, viewType } = useUserSettings();
  const [openSearch, setOpenSearch] = React.useState(false);

  const { data: logoData, isLoading } = useQuery(
    getUserPortalSettingsForUserQuery(isUserPortal)
  );

  const { colorMode, setColorMode, notifications } = useStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const handleKeyDown = (event: KeyboardEvent) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    if (
      (isMac && event.metaKey && event.key === 'k') ||
      (!isMac && event.ctrlKey && event.key === 'k')
    ) {
      event.preventDefault();
      setOpenSearch(true);
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const [notificationEl, setNotificationEl] = useState<null | HTMLElement>(
    null
  );
  const isNotificationOpen = Boolean(notificationEl);

  const handleNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setNotificationEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationEl(null);
  };

  function handleThemeButtonClick() {
    setColorMode(colorMode === 'dark' ? 'light' : 'dark');

    if (!viewType) {
      return;
    }

    const updatedViewType = {
      ...viewType,
      colorMode: colorMode === 'dark' ? 'light' : ('dark' as 'dark' | 'light'),
    };

    saveViewType(updatedViewType);

    queryClient.setQueryData<UserSettings | undefined>(
      [UserKeys.UserSettings],
      (oldData) => {
        if (!oldData) {
          return;
        }

        return {
          ...oldData,
          viewType: updatedViewType,
        };
      }
    );
  }

  const menuId = 'primary-search-account-menu';
  const hasDarkModeLogo =
    logoData?.darkModeLogoUrl && logoData.darkModeLogoUrl !== '';
  const hasLightModeLogo = logoData?.logoUrl && logoData.logoUrl !== '';

  return (
    <Box>
      <Stack
        component="main"
        direction="row"
        justifyContent="space-between"
        alignItems="start"
      >
        {isUserPortal ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            {isLoading ? (
              <Box width={200} height={80} />
            ) : (
              <Box
                style={{
                  position: 'relative',
                  width: '200px',
                  height: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {/* Default dark mode logo */}
                {!hasDarkModeLogo && colorMode === 'dark' && (
                  <img
                    src={GpanelLogoWhite}
                    alt="Logo"
                    height={80}
                    width={200}
                    style={{
                      position: 'absolute',
                      objectFit: 'contain',
                      objectPosition: 'left',
                    }}
                  />
                )}
                {/* Default light mode logo */}
                {!hasLightModeLogo && colorMode === 'light' && (
                  <img
                    src={GpanelLogo}
                    alt="Logo"
                    height={80}
                    width={200}
                    style={{
                      position: 'absolute',
                      objectFit: 'contain',
                      objectPosition: 'left',
                    }}
                  />
                )}
                {/* Custom dark mode logo */}
                {hasDarkModeLogo && colorMode === 'dark' && (
                  <img
                    src={logoData.darkModeLogoUrl}
                    alt="Logo"
                    height={80}
                    width={200}
                    style={{
                      position: 'absolute',
                      objectFit: 'contain',
                      objectPosition: 'left',
                    }}
                  />
                )}
                {/* Custom light mode logo */}
                {hasLightModeLogo && colorMode === 'light' && (
                  <img
                    src={logoData.logoUrl}
                    alt="Logo"
                    height={80}
                    width={200}
                    style={{
                      position: 'absolute',
                      objectFit: 'contain',
                      objectPosition: 'left',
                    }}
                  />
                )}
              </Box>
            )}
          </Stack>
        ) : (
          <span></span>
        )}

        <Stack flexDirection="row" alignItems="center" gap={1}>
          {currentTier === 'trial' && <TrialButton />}
          {!isUserPortal && (
            <Tooltip title={`Search ${isMac ? '⌘K' : '(ctrl+k)'}`} arrow>
              <IconButton onClick={() => setOpenSearch(true)}>
                <SearchOutlined />
              </IconButton>
            </Tooltip>
          )}
          {openSearch && (
            <SearchDialog
              open={openSearch}
              handleClose={() => setOpenSearch(false)}
            />
          )}
          {colorMode === 'dark' ? (
            <Tooltip title="Switch to Light Mode" arrow>
              <IconButton onClick={handleThemeButtonClick}>
                <LightModeOutlined />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Switch to Dark Mode" arrow>
              <IconButton onClick={handleThemeButtonClick}>
                <DarkModeOutlined />
              </IconButton>
            </Tooltip>
          )}

          {/* {!isUserPortal && (
            <Tooltip title="Live Support" arrow>
              <IconButton
                onClick={() => {
                  const status = window.HubSpotConversations?.widget?.status();

                  if (status?.loaded) {
                    window.HubSpotConversations?.widget?.remove();
                  } else {
                    window.HubSpotConversations?.widget?.load();
                    window.HubSpotConversations?.widget?.open();
                  }
                }}
              >
                <SupportAgent />
              </IconButton>
            </Tooltip>
          )} */}

          <SupportAgentNavToggle />

          <Tooltip title="Notifications" arrow>
            <IconButton
              aria-label="show new notifications"
              aria-controls={
                isNotificationOpen ? 'notification-menu' : undefined
              }
              aria-haspopup="true"
              aria-expanded={isNotificationOpen ? 'true' : undefined}
              onClick={handleNotificationClick}
            >
              <Badge
                badgeContent={notifications.filter((n) => !n.read).length}
                color="error"
              >
                <NotificationsOutlined />
              </Badge>
            </IconButton>
          </Tooltip>
          <NotificationPopout
            open={isNotificationOpen}
            handleClose={() => handleNotificationClose()}
            anchorEl={notificationEl}
          />

          <Tooltip title="User Settings" arrow>
            <IconButton
              size="small"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Badge
                color="primary"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                badgeContent={'Trial'}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: 10,
                    display: currentTier === 'trial' ? 'block' : 'none',
                  },
                }}
              >
                <UserAvatar
                  userEmail={loggedInUser}
                  isUserPortal={isUserPortal}
                />
              </Badge>
            </IconButton>
          </Tooltip>
          <UserPopout
            open={isMenuOpen}
            handleClose={() => handleMenuClose()}
            anchorEl={anchorEl}
            isUserPortal={isUserPortal}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
